import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Modal,
    Input,
    DatePicker,
    Select,
    Space,
    Tag,
    Button,
    Row,
    Col,
    Typography,
    Tooltip,
    message,
} from 'antd';
import Table from 'components/Common/Table';
import {
    SearchOutlined,
    BankOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    DollarOutlined,
    FileTextOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import * as adminactions from "store/actions/AuthActions";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const ORGANIZATION_UUID = process.env.REACT_APP_ORGANIZATION_UUID;

const BankTransactionSelectModal = ({
    visible,
    onClose,
    onSelect,
    selectedTransactionId,
    title = "Select Bank Transaction",
}) => {
    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    // A ref, so the debounced search (created once) fetches with the current size.
    const pageSizeRef = useRef(10);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [dateRange, setDateRange] = useState([null, null]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedBankAccount, setSelectedBankAccount] = useState(null);
    const [selectedRowKey, setSelectedRowKey] = useState(null);
    const [initialized, setInitialized] = useState(false);

    // Fetch bank accounts for filter dropdown
    const fetchBankAccounts = async () => {
        try {
            const data = await adminactions.loadBankAccounts(ORGANIZATION_UUID);
            if (data) {
                setBankAccounts(data.results || data || []);
            }
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
        }
    };

    // Fetch transactions
    const fetchTransactions = async (page = 1, search = '', dates = [null, null], bankAccount = null, pageSize = pageSizeRef.current) => {
        pageSizeRef.current = pageSize;
        setLoading(true);
        try {
            const params = {
                organization: ORGANIZATION_UUID,
                page,
                page_size: pageSize,
            };

            if (search) {
                params.search = search;
            }
            if (dates && dates[0] && dates[1]) {
                params.transaction_date__gte = dates[0].format('YYYY-MM-DD');
                params.transaction_date__lte = dates[1].format('YYYY-MM-DD');
            }
            if (bankAccount) {
                params.bank_account = bankAccount;
            }

            console.log('Fetching bank transactions with params:', params);
            const data = await adminactions.loadBankTransactions(params);
            console.log('Bank transactions response:', data);

            if (data) {
                setTransactions(data.results || data || []);
                setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize,
                    total: data.count || 0,
                }));
            }
        } catch (error) {
            console.error('Error fetching bank transactions:', error);
            message.error('Failed to fetch bank transactions');
        } finally {
            setLoading(false);
        }
    };

    // When modal opens, fetch data
    useEffect(() => {
        if (visible && !initialized) {
            console.log('Modal opened, fetching data...');
            setInitialized(true);
            fetchBankAccounts();
            fetchTransactions(1, '', [null, null], null);
            setSelectedRowKey(selectedTransactionId || null);
        }

        // Reset when modal closes
        if (!visible && initialized) {
            setInitialized(false);
            setSearchInputValue('');
            setDateRange([null, null]);
            setSelectedBankAccount(null);
            setTransactions([]);
        }
    }, [visible]);

    // Debounced search
    const debouncedFetch = useMemo(
        () => debounce((search, dates, bankAccount) => {
            fetchTransactions(1, search, dates, bankAccount);
        }, 500),
        []
    );

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        debouncedFetch(value, dateRange, selectedBankAccount);
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        const newDates = dates || [null, null];
        setDateRange(newDates);
        fetchTransactions(1, searchInputValue, newDates, selectedBankAccount);
    };

    // Handle bank account filter change
    const handleBankAccountChange = (value) => {
        setSelectedBankAccount(value || null);
        fetchTransactions(1, searchInputValue, dateRange, value || null);
    };

    const handleTableChange = (paginationConfig) => {
        const size = paginationConfig.pageSize;
        // Back to page 1 when the page size changes.
        const page = size !== pageSizeRef.current ? 1 : paginationConfig.current;
        fetchTransactions(page, searchInputValue, dateRange, selectedBankAccount, size);
    };

    const handleReset = () => {
        setSearchInputValue('');
        setDateRange([null, null]);
        setSelectedBankAccount(null);
        fetchTransactions(1, '', [null, null], null);
    };

    const handleSelect = () => {
        const selectedTxn = transactions.find(t => t.id === selectedRowKey);
        onSelect(selectedRowKey, selectedTxn);
        onClose();
    };

    const getAttachmentTags = (record) => {
        const tags = [];

        // Check for linked payments (can be array or single value)
        const paymentIds = record.reconciled_with_payment_id;
        const hasPayments = Array.isArray(paymentIds) ? paymentIds.length > 0 : !!paymentIds;
        if (hasPayments || record.reconciled_with_payment) {
            const count = Array.isArray(paymentIds) ? paymentIds.length : 1;
            tags.push(
                <Tooltip title={`Linked to ${count} Payment${count > 1 ? 's' : ''}`} key="payment">
                    <Tag color="blue" icon={<DollarOutlined />}>
                        Payment{count > 1 ? ` (${count})` : ''}
                    </Tag>
                </Tooltip>
            );
        }

        // Check for linked expenses (can be array or single value)
        const expenseIds = record.reconciled_with_expense_id;
        const hasExpenses = Array.isArray(expenseIds) ? expenseIds.length > 0 : !!expenseIds;
        if (hasExpenses || record.reconciled_with_expense) {
            const count = Array.isArray(expenseIds) ? expenseIds.length : 1;
            tags.push(
                <Tooltip title={`Linked to ${count} Expense${count > 1 ? 's' : ''}`} key="expense">
                    <Tag color="orange" icon={<FileTextOutlined />}>
                        Expense{count > 1 ? ` (${count})` : ''}
                    </Tag>
                </Tooltip>
            );
        }

        // Check for linked payroll/salary (can be array or boolean)
        const payrollIds = record.linked_payroll;
        const hasPayroll = Array.isArray(payrollIds) ? payrollIds.length > 0 : (!!payrollIds || record.reconciled_with_salary);
        if (hasPayroll) {
            const count = Array.isArray(payrollIds) ? payrollIds.length : 1;
            tags.push(
                <Tooltip title={`Linked to ${count} Salary Record${count > 1 ? 's' : ''}`} key="salary">
                    <Tag color="purple" icon={<TeamOutlined />}>
                        Salary{count > 1 ? ` (${count})` : ''}
                    </Tag>
                </Tooltip>
            );
        }

        if (tags.length === 0) {
            return <Tag color="default">Available</Tag>;
        }

        return <Space size={4}>{tags}</Space>;
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'transaction_date',
            key: 'transaction_date',
            width: 100,
            render: (date) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {dayjs(date).format('DD/MM/YYYY')}
                </span>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'transaction_type',
            key: 'transaction_type',
            width: 100,
            render: (type) => (
                <Tag color={type === 'DEPOSIT' ? 'green' : 'red'}>
                    {type}
                </Tag>
            ),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right',
            render: (amount, record) => (
                <Text style={{
                    color: record.transaction_type === 'DEPOSIT' ? '#52c41a' : '#f5222d',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                }}>
                    {record.transaction_type === 'DEPOSIT' ? '+' : '-'}₹{parseFloat(amount).toLocaleString('en-IN')}
                </Text>
            ),
        },
        {
            title: 'Balance',
            dataIndex: 'balance',
            key: 'balance',
            width: 120,
            align: 'right',
            render: (balance) => balance ? (
                <span style={{ whiteSpace: 'nowrap' }}>
                    ₹{parseFloat(balance).toLocaleString('en-IN')}
                </span>
            ) : '-',
        },
        {
            title: 'Remarks',
            dataIndex: 'remarks',
            key: 'remarks',
            width: 250,
            ellipsis: true,
            render: (remarks) => (
                <Tooltip title={remarks}>
                    <span>{remarks || '-'}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Cheque/Ref',
            dataIndex: 'cheque_ref_number',
            key: 'cheque_ref_number',
            width: 120,
            ellipsis: true,
            render: (ref) => ref || '-',
        },
        {
            title: 'Linked To',
            key: 'linked_to',
            width: 150,
            render: (_, record) => getAttachmentTags(record),
        },
    ];

    const rowSelection = {
        type: 'radio',
        selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
        onChange: (selectedRowKeys) => {
            setSelectedRowKey(selectedRowKeys[0] || null);
        },
    };

    return (
        <Modal
            title={
                <Space>
                    <BankOutlined />
                    <span>{title}</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={1100}
            destroyOnClose={false}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="clear"
                    onClick={() => {
                        setSelectedRowKey(null);
                        onSelect(null, null);
                        onClose();
                    }}
                >
                    Clear Selection
                </Button>,
                <Button
                    key="select"
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleSelect}
                    disabled={!selectedRowKey}
                >
                    Select Transaction
                </Button>,
            ]}
        >
            {/* Filters */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8}>
                    <Input
                        placeholder="Search remarks, ref number..."
                        prefix={<SearchOutlined />}
                        allowClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                    />
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <RangePicker
                        style={{ width: '100%' }}
                        placeholder={['Start Date', 'End Date']}
                        value={dateRange}
                        onChange={handleDateRangeChange}
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select Bank Account"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        value={selectedBankAccount}
                        onChange={handleBankAccountChange}
                        options={bankAccounts.map(ba => ({
                            value: ba.id,
                            label: `${ba.bank_name || ba.bank} - ${ba.account_number}`,
                        }))}
                    />
                </Col>
                <Col xs={24} sm={12} md={2}>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                        Reset
                    </Button>
                </Col>
            </Row>

            {/* Summary */}
            <div style={{ marginBottom: 12 }}>
                <Text type="secondary">
                    Showing {transactions.length} of {pagination.total} transactions
                    {selectedRowKey && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                            1 selected
                        </Tag>
                    )}
                </Text>
            </div>

            {/* Table */}
            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={transactions}
                rowKey="id"
                loading={loading}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }}
                onChange={handleTableChange}
                scroll={{ x: 1000, y: 400 }}
                size="small"
                onRow={(record) => ({
                    onClick: () => setSelectedRowKey(record.id),
                    style: { cursor: 'pointer' },
                })}
                rowClassName={(record) =>
                    record.id === selectedRowKey ? 'ant-table-row-selected' : ''
                }
            />
        </Modal>
    );
};

export default BankTransactionSelectModal;
